/** A row from `GET /loan/termChanges`. */
export interface TermChangeRow {
  id: number
  loanNumber: number
  /** `rate`, `payment` or `escrow`. */
  field: string
  fieldLabel: string
  effectiveDate: string
  value: number
  previousValue: number | null
  /** `scheduled`, `applied` or `cancelled`. */
  status: string
  source: string
  sourceLabel: string
  note: string
  createdBy: string
  appliedAt: string | null
  cancelledBy: string
  /** Borrower notices produced for this change, oldest first; changes on one date share one. */
  noticeFiles?: Array<{ fileName: string; fileKey: string; createdAt: string; emailedTo?: string; emailedAt?: string }>
}

export interface TermChangeSettings {
  rateChangeAccrualText: string
  rateChangeAccrualFromLoan: boolean
  rateChangeRecastText: string
  rateChangeRecastFromLoan: boolean
}

export const FIELD_LABELS: Record<string, string> = {
  rate: 'Interest Rate',
  payment: 'Principal & Interest',
  escrow: 'Impound (Escrow) Monthly',
}

/** A rate reads as a percentage, a payment or impound as dollars. */
export const formatTermValue = (field: string, value: number | null | undefined) => {
  if (value == null) return ''
  if (field === 'rate') return `${Number(value)}%`
  return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * A field's value on `date` from the loan's changes, mirroring the backend `TermTimeline.valueOn`:
 * the latest non-cancelled change effective on or before the date, and of two on that date the one
 * entered last; before the first one, what it replaced if it has been applied; otherwise null (the
 * loan's current value stands).
 */
export const termValueOn = (changes: TermChangeRow[], field: string, date: string): number | null => {
  const rows = (changes || [])
    .filter((c) => c.field === field && c.status !== 'cancelled')
    .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate) || a.id - b.id)
  if (!rows.length) return null
  let found: TermChangeRow | null = null
  for (const row of rows) if (row.effectiveDate <= date) found = row
  if (found) return Number(found.value)
  const first = rows[0]
  return first.status === 'applied' && first.previousValue != null ? Number(first.previousValue) : null
}
